import { create } from 'zustand'
import type { User, Project, Notification } from '@/types'

interface AppState {
  // User state
  user: User | null
  setUser: (user: User | null) => void

  // Projects state
  projects: Project[]
  setProjects: (projects: Project[]) => void
  addProject: (project: Project) => void
  updateProject: (id: string, updates: Partial<Project>) => void
  deleteProject: (id: string) => void

  // Notifications state
  notifications: Notification[]
  addNotification: (notification: Notification) => void
  markNotificationAsRead: (id: string) => void
  clearNotifications: () => void

  // UI state
  isSidebarOpen: boolean
  toggleSidebar: () => void
}

export const useStore = create<AppState>((set) => ({
  // User state (managed by auth context, not persisted here)
  user: null,
  setUser: (user) => {
    set({ user })
  },

  // Projects state - In-memory only, fetched from API
  // Use getProjects() API to fetch, this store is for UI state only
  projects: [],
  setProjects: (projects) => {
    set({ projects })
  },
  addProject: (project) =>
    set((state) => ({
      projects: [...state.projects, project]
    })),
  updateProject: (id, updates) =>
    set((state) => ({
      projects: state.projects.map((p) => 
        p.id === id ? { ...p, ...updates } : p
      )
    })),
  deleteProject: (id) =>
    set((state) => ({
      projects: state.projects.filter((p) => p.id !== id)
    })),

  // Notifications state
  notifications: [],
  addNotification: (notification) =>
    set((state) => ({
      notifications: [notification, ...state.notifications],
    })),
  markNotificationAsRead: (id) =>
    set((state) => ({
      notifications: state.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
    })),
  clearNotifications: () => set({ notifications: [] }),

  // UI state
  isSidebarOpen: true,
  toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
}))
